/* eslint-disable react-hooks/exhaustive-deps */
import queryString from 'querystring';
import { useEffect, useRef, useState } from 'react';
import { useLocation, useHistory, useRouteMatch } from 'react-router-dom';
import debounce from 'debounce';
// import { downloadXlsx } from '../utils/download';
import { useIsMounted } from './useIsMounted';

const useInitialRenderEffect = () => {
  const initialRender = useRef(true);

  useEffect(() => {
    initialRender.current = false;
  }, []);

  return initialRender.current;
};

let debouncedFn;
const usePaginatedSearch = ({
  serviceArgs = [],
  fetchObjectsByIds,
  fetchObjects,
  searchDocs,
  searchArgs = {},
  searchType,
  followsLoading = false,
  followingFiltered,
  showArchived = false,
  sourceTypes = null,
  setFollowingFiltered = null,
}) => {
  const history = useHistory();
  const { url } = useRouteMatch();
  const { search, pathname: currentURL } = useLocation();
  const isMounted = useIsMounted();

  const query = queryString.parse(search.slice(1));
  const [loading, setLoading] = useState(null);
  const [totalDocs, setTotalDocs] = useState(0);
  const [links, setLinks] = useState(null);
  const [objects, setObjects] = useState([]);
  const [objectIds, setObjectIds] = useState(null);

  const [autoCompleteTerms, setAutoCompleteTerms] = useState('');

  const processResponse = ({ data, totalDocs: td, links: l }) => {
    if (isMounted()) {
      setObjects(data);
      setTotalDocs(td);
      setLinks(l);
      setLoading(false);
    }
  };

  const fetchDataForSearch = ({ searchParams }) => {
    if (objectIds?.length) {
      return fetchObjectsByIds(...serviceArgs, objectIds, searchParams);
    }
    return { data: [], totalDocs: 0, links: null };
  };

  const fetchData = async ({ searchParams = search } = {}) => {
    if (autoCompleteTerms) {
      let updatedSearchParams = searchParams;
      if (showArchived) {
        updatedSearchParams = searchParams ? `${searchParams}&showArchived=true` : '?showArchived=true';
      }
      return fetchDataForSearch({ searchParams: updatedSearchParams });
    }
    const params = new URLSearchParams(searchParams);
    params.set('following', String(!!followingFiltered));
    if (showArchived) params.set('showArchived', 'true');
    return fetchObjects(...serviceArgs, `?${params.toString()}`);
  };

  const refreshObjects = () => {
    if (isMounted()) {
      setLoading(true);
      if (followsLoading) return;

      fetchData().then(processResponse);
    }
  };

  const parseStatsForDownload = ({ stats, columns }) => {
    return stats.map((stat) => {
      return Object.keys(columns)
        .slice(1)
        .map((column) => {
          const { field } = columns[column];
          return { v: stat[field || columns[column]], ...columns[column].downloadOpts };
        });
    });
  };

  const onDownloadClick = async ({ columns, filename, downloadXlsx }) => {
    const params = new URLSearchParams(search);
    params.set('page[size]', '0');
    params.set('page[after]', '1');

    const stats = await fetchData({ searchParams: `?${params.toString()}` });
    const statsForDownload = parseStatsForDownload({ stats: stats.data, columns });
    const headers = Object.keys(columns)
      .slice(1)
      .map((column) => {
        return column === 'title' ? 'Name' : column;
      });

    downloadXlsx({ data: [{ name: 'Summary', data: [headers, ...statsForDownload] }], filename });
  };

  const handleChange = (event, value) => {
    if (value === null) return;
    const params = new URLSearchParams(search);
    params.set('page[after]', value);
    history.push({ search: `?${decodeURI(params.toString())}` });
  };

  const activePage = currentURL === url;

  useEffect(() => {
    refreshObjects();
  }, [JSON.stringify(serviceArgs), JSON.stringify(objectIds), query['page[after]']]);

  useEffect(() => {
    if (activePage && followingFiltered !== null) {
      if (followingFiltered && query['page[after]'] && query['page[after]'] !== '1') {
        handleChange(null, 1);
      } else {
        refreshObjects();
      }
    }
  }, [followingFiltered]);

  const initialRender = useInitialRenderEffect();

  useEffect(() => {
    if (activePage) {
      if (!initialRender) {
        handleChange(null, 1);
        refreshObjects();
      }
    }
  }, [query.sort]);

  const onSearchClear = () => {
    setAutoCompleteTerms('');
    setObjectIds(null);
  };

  const onSearchChange = (event) => {
    if (event.persist) event.persist();

    const {
      target: { value },
    } = event;
    setAutoCompleteTerms(value);

    if (!debouncedFn) {
      debouncedFn = debounce(
        ({
          terms,
          navigate,
          setFn,
          type,
          searchService,
          searchServiceArgs,
          scopedUrl,
          useArchived,
          scopedSourceTypes,
          scopedSetFollowingFiltered,
        }) => {
          if (terms === '') {
            setFn(null);
            return;
          }
          const searchTerms = {
            ...searchServiceArgs,
            search: {
              terms,
              type,
              showArchived: useArchived,
              sourceTypes: scopedSourceTypes?.length ? scopedSourceTypes : null,
            },
          };

          searchService(searchTerms).then((results) => {
            const ids = results.map(({ _id }) => _id);
            setFn(ids);
            if (scopedSetFollowingFiltered) scopedSetFollowingFiltered(false);
            // keeps the sourceType query params if you're in Content List
            if (scopedSourceTypes?.length) {
              const params = new URLSearchParams();
              if (Array.isArray(scopedSourceTypes)) {
                scopedSourceTypes.forEach((currentSourceType) => {
                  params.append('sourceType', currentSourceType);
                });
              } else {
                params.set('sourceType', scopedSourceTypes);
              }
              if (navigate) history.push(`${scopedUrl}?${params.toString()}`);
            } else if (navigate) {
              // clears the query params
              history.push(scopedUrl);
            }
          });
        },
        100
      );
    }

    debouncedFn({
      terms: value,
      navigate: search,
      setFn: setObjectIds,
      type: searchType,
      searchService: searchDocs,
      searchServiceArgs: searchArgs,
      scopedUrl: url,
      useArchived: showArchived,
      scopedSourceTypes: sourceTypes,
      scopedSetFollowingFiltered: setFollowingFiltered,
    });
  };

  useEffect(() => {
    if (!initialRender) {
      if (autoCompleteTerms) {
        onSearchChange({ target: { value: autoCompleteTerms } });
      } else {
        refreshObjects();
      }
    }
  }, [followsLoading, showArchived, autoCompleteTerms]);

  const handleDialogClose = (event, reason, opts = {}) => {
    const { url: customUrl } = opts;
    const returnUrl = customUrl || url;
    if (['backdropClick', 'cancel'].includes(reason)) {
      history.push(`${returnUrl}${search}`);
      return;
    }

    refreshObjects();

    const { search: customSearch, wizard } = opts;

    if (!wizard) {
      history.push(`${returnUrl}${customSearch !== undefined ? customSearch : search}`);
    }
  };

  return {
    totalDocs,
    links,
    objects,
    setObjectIds,
    handleDialogClose,
    handleChange,
    onSearchChange,
    onSearchClear,
    autoCompleteTerms,
    setAutoCompleteTerms,
    loading,
    onDownloadClick,
    refreshObjects,
  };
};

export default usePaginatedSearch;
